// src/components/Reports.js
import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../../navigation/AuthProvider";
import { Toast } from "../../components/ToastPopup";
import Nav from "../../components/nav";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaHourglassHalf,
  FaEye,
  FaEyeSlash,
  FaFilter,
  FaFileAlt,
  FaTimes,
  FaClock,
  FaTag,
  FaCommentAlt,
  FaUser,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes as FaTimesCircle,
} from "react-icons/fa";

import axios from "axios";
import { BACKEND_URL } from "../../constants";

const PAGE_SIZE = 20;



// 🔥 UNIVERSAL TIME NORMALIZER
const normalizeTime = (value) => {
  if (!value) return null;

  // Already HH:mm
  if (typeof value === "string" && /^\d{2}:\d{2}$/.test(value)) {
    return value;
  }

  // ISO / Date → HH:mm
  const d = new Date(value);
  if (!isNaN(d)) {
    return d.toLocaleTimeString("sv-SE", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }

  return null;
};

// Helper: format date and time
const formatDateTime = (value) => {
  if (!value) return "-";

  const d = new Date(value);

  // ISO datetime
  if (!isNaN(d)) {
    return (
      d.toLocaleDateString("sv-SE") +
      " • " +
      d.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })
    );
  }

  // HH:mm → visa bara tiden
  const time = normalizeTime(value);
  if (time) return time;

  return "-";
};



const formatReportDate = (value) => {
  if (!value) return "-";

  const d = new Date(value);
  if (!isNaN(d)) {
    return d.toLocaleDateString("sv-SE");
  }

  return value; // fallback if already string YYYY-MM-DD
};

// Helper: format time only
const formatTime = (date) => {
  const d = new Date(date);
  return d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
};

// Helper: calculate hours from start and end (handles overnight correctly)
const calculateHours = (start, end) => {
  if (!start || !end) return 0;

  const parseTime = (value) => {
    // HH:mm
    if (typeof value === "string" && /^\d{2}:\d{2}$/.test(value)) {
      const [h, m] = value.split(":").map(Number);
      return h * 60 + m;
    }

    // ISO datetime eller Date
    const d = new Date(value);
    if (!isNaN(d)) {
      return d.getHours() * 60 + d.getMinutes();
    }

    return null;
  };

  const startMinutes = parseTime(start);
  const endMinutes = parseTime(end);

  if (startMinutes === null || endMinutes === null) return 0;

  let diff = endMinutes - startMinutes;

  if (diff < 0) diff += 24 * 60; // overnight

  return diff / 60;
};


const calculateBreakHours = (breaks = []) => {
  if (!Array.isArray(breaks)) return 0;

  return breaks.reduce((sum, breakItem) => {
    return (
      sum +
      calculateHours(
        breakItem.startTime,
        breakItem.endTime
      )
    );
  }, 0);
};

const calculateNetHours = (report) => {
  const totalHours = calculateHours(
    report.startTime,
    report.endTime
  );

  const breakHours = calculateBreakHours(report.breaks);

  return Math.max(0, totalHours - breakHours);
};

export const Reports = () => {
  const { user, reports, company, employees, reportCategories, projects, setReportCategories } = useAuth();

  // States
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [readStatusData, setReadStatusData] = useState([]);
  const [readFilter, setReadFilter] = useState("all");
  const [summary, setSummary] = useState({
    totalReports: 0,
    readReports: 0,
    unreadReports: 0,
  });
  const [selectedReport, setSelectedReport] = useState(null);
  const [editingReport, setEditingReport] = useState(null); // rapport som redigeras
  const [editedReportData, setEditedReportData] = useState(null); // temporär data för redigering

  // Popup för kategorihantering
  const [showCategoryPopup, setShowCategoryPopup] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [editedCategoryName, setEditedCategoryName] = useState("");
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  // Bekräftelse för borttagning av rapport
  const [showDeleteReportConfirm, setShowDeleteReportConfirm] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);

  // Kategorier
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!reportCategories) return;
    const arr = Array.isArray(reportCategories)
      ? reportCategories
      : Object.values(reportCategories);
    setCategories(arr);
  }, [reportCategories]);

  const [employeeInput, setEmployeeInput] = useState("");
  const [projectInput, setProjectInput] = useState("");

  const monthsInSwedish = [
    "Januari","Februari","Mars","April","Maj","Juni",
    "Juli","Augusti","September","Oktober","November","December"
  ];

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/get-reports`, {
        userId: user._id,
        companyId: company._id,
        month: selectedMonth,
        year: selectedYear,
        readStatus: readFilter === "all" ? "" : readFilter,
      }, { withCredentials: true });

      if (res.data.status === "success") {
        setReadStatusData(res.data.reports);
        updateSummary(res.data.reports);
      }
    } catch (err) {
      console.error("❌ Failed to load reports", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!company?._id || !user?._id) return;
    fetchReports();
  }, [selectedMonth, selectedYear, readFilter, company]);

  // Styles
  const styles = {
    wrap: { width: "100%", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" },
    container: { margin: "0 auto", padding: "24px 32px", maxWidth: 1600 },

    header: { marginBottom: 32 },
    pageTitle: { margin: 0, fontSize: 32, fontWeight: 700, color: "#1a1a1a", letterSpacing: "-0.02em" },
    pageSubtitle: { margin: "4px 0 0", fontSize: 16, color: "#64748b", fontWeight: 400 },

    statsContainer: { display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 },
    statCard: { 
      background: "white", 
      padding: "20px 24px", 
      borderRadius: 12, 
      border: "1px solid #e2e8f0", 
      flex: 1, 
      minWidth: 200,
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)" 
    },
    statValue: { fontSize: 32, fontWeight: 700, color: "#1a1a1a", lineHeight: 1.2 },
    statLabel: { fontSize: 13, color: "#64748b", fontWeight: 500, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" },
    statIcon: { fontSize: 24, color: "#2563eb", marginBottom: 12 },

    controlsSection: { 
      display: "flex", 
      justifyContent: "space-between", 
      alignItems: "center", 
      marginBottom: 24, 
      gap: 16, 
      flexWrap: "wrap",
      background: "white",
      padding: "20px",
      borderRadius: 12,
      border: "1px solid #e5e7eb",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    },
    dateSelector: { display: "flex", alignItems: "center", gap: 12 },
    select: { 
      background: "white", 
      border: "1px solid #d1d5db", 
      color: "#374151", 
      padding: "8px 12px", 
      borderRadius: 6, 
      outline: "none", 
      fontSize: 14, 
      fontWeight: 500, 
      minWidth: 140 
    },
    filterGroup: { display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" },

    categoryFilterWrapper: {
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    
    manageCategoriesButton: {
      background: "#f1f5f9",
      border: "1px dashed #94a3b8",
      color: "#64748b",
      padding: "8px 12px",
      borderRadius: 6,
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 6,
      transition: "all 0.2s",
      whiteSpace: "nowrap",
      ":hover": {
        background: "#e2e8f0",
        color: "#334155",
        borderColor: "#64748b",
      },
    },

    readFilterGroup: {
      display: "flex",
      gap: 8,
      background: "#f1f5f9",
      padding: "4px",
      borderRadius: 8,
    },
    readFilterButton: (active) => ({
      padding: "6px 12px",
      borderRadius: 6,
      border: "none",
      background: active ? "white" : "transparent",
      color: active ? "#2563eb" : "#64748b",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 6,
      boxShadow: active ? "0 2px 4px rgba(0,0,0,0.05)" : "none",
    }),

    tableContainer: { 
      background: "white", 
      borderRadius: 12, 
      border: "1px solid #e5e7eb", 
      overflow: "hidden", 
      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", 
      marginBottom: 24 
    },
    tableHeader: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1.3fr) minmax(0,1.3fr) minmax(0,1.3fr) minmax(0,1.2fr) minmax(0,0.8fr) minmax(0,1.2fr) minmax(0,1.8fr)",
      gap: 16,
      padding: "14px 20px",
      color: "#374151",
      fontSize: 12,
      fontWeight: 700,
      background: "#f8fafc",
      borderBottom: "1px solid #e5e7eb",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    },
    tableRow: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1.3fr) minmax(0,1.3fr) minmax(0,1.3fr) minmax(0,1.2fr) minmax(0,0.8fr) minmax(0,1.2fr) minmax(0,1.8fr)",
      gap: 16,
      alignItems: "center",
      padding: "16px 20px",
      borderBottom: "1px solid #f3f4f6",
      fontSize: 14,
      transition: "background-color 0.12s ease",
      background: "white",
      cursor: "pointer",
      ":hover": {
        background: "#fafafa",
      },
    },
    emptyState: { padding: "48px 20px", color: "#6b7280", fontSize: 14, textAlign: "center" },

    cell: { minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
    cellRight: { textAlign: "right" },

    categoryBadge: {
      display: "inline-block",
      padding: "4px 10px",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 600,
    },

    readStatus: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
    },
    readStatusRead: {
      background: "#f0fdf4",
      color: "#166534",
      border: "1px solid #bbf7d0",
    },
    readStatusUnread: {
      background: "#fef2f2",
      color: "#991b1b",
      border: "1px solid #fecaca",
    },

    actionButton: {
      padding: "6px 10px",
      borderRadius: 6,
      border: "none",
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      transition: "all 0.2s",
      whiteSpace: "nowrap",
    },
    markReadButton: {
      background: "#2563eb",
      color: "white",
      ":hover": { background: "#1d4ed8" },
    },
    markUnreadButton: {
      background: "#f1f5f9",
      color: "#64748b",
      ":hover": { background: "#e2e8f0" },
    },
    editButton: {
      background: "#f1f5f9",
      color: "#374151",
      ":hover": { background: "#e2e8f0" },
    },
    deleteButton: {
      background: "#fee2e2",
      color: "#b91c1c",
      ":hover": { background: "#fecaca" },
    },

    pagination: { 
      display: "flex", 
      alignItems: "center", 
      justifyContent: "space-between", 
      padding: "16px 20px", 
      background: "white", 
      border: "1px solid #e5e7eb", 
      borderRadius: 8,
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    },
    paginationInfo: { fontSize: 14, color: "#6b7280", fontWeight: 500 },
    paginationControls: { display: "flex", alignItems: "center", gap: 8 },
    pageBtn: {
      borderRadius: 6,
      border: "1px solid #d1d5db",
      background: "white",
      color: "#374151",
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 700,
      height: 32,
      minWidth: 100,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    },

    // Popup styles
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      backdropFilter: "blur(4px)",
    },
    popup: {
      background: "white",
      borderRadius: 16,
      width: "90%",
      maxWidth: 600,
      maxHeight: "90vh",
      overflow: "auto",
      boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
    },
    popupHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "20px 24px",
      borderBottom: "1px solid #e5e7eb",
    },
    popupTitle: {
      fontSize: 20,
      fontWeight: 700,
      color: "#1a1a1a",
      margin: 0,
    },
    closeButton: {
      background: "transparent",
      border: "none",
      fontSize: 20,
      cursor: "pointer",
      color: "#9ca3af",
      padding: "4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 6,
      ":hover": {
        background: "#f3f4f6",
        color: "#4b5563",
      },
    },
    popupContent: {
      padding: "24px",
    },
    
    // Form styles
    addCategorySection: {
      marginBottom: 32,
      padding: "20px",
      background: "#f8fafc",
      borderRadius: 12,
      border: "1px solid #e2e8f0",
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: 700,
      color: "#1a1a1a",
      margin: "0 0 16px 0",
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    formGroup: {
      display: "flex",
      flexDirection: "column",
      gap: 16,
      marginBottom: 0,
    },
    formRow: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },
    // Ny rad för två kolumner
    formRowDouble: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 12,
    },
    label: {
      fontSize: 13,
      fontWeight: 600,
      color: "#4b5563",
    },
    input: {
      width: "100%",
      padding: "10px 14px",
      fontSize: 15,
      border: "1px solid #d1d5db",
      borderRadius: 8,
      outline: "none",
      transition: "border-color 0.2s",
      ":focus": {
        borderColor: "#2563eb",
      },
    },
    textarea: {
      padding: "10px 14px",
      fontSize: 15,
      border: "1px solid #d1d5db",
      borderRadius: 8,
      outline: "none",
      minHeight: 80,
      maxHeight: 120,
      resize: "vertical",
    },
    addButton: {
      padding: "10px 20px",
      background: "#2563eb",
      border: "none",
      borderRadius: 8,
      fontSize: 14,
      fontWeight: 600,
      color: "white",
      cursor: "pointer",
      transition: "all 0.2s",
      display: "flex",
      alignItems: "center",
      gap: 6,
      whiteSpace: "nowrap",
      ":hover": {
        background: "#1d4ed8",
      },
    },
    cancelButton: {
      padding: "10px 20px",
      background: "#f1f5f9",
      border: "1px solid #e2e8f0",
      borderRadius: 8,
      fontSize: 14,
      fontWeight: 600,
      color: "#64748b",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 4,
      ":hover": {
        background: "#e2e8f0",
      },
    },

    // Category list styles
    categoryList: {
      display: "flex",
      flexDirection: "column",
      gap: 8,
    },
    categoryItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "12px 16px",
      background: "white",
      border: "1px solid #e5e7eb",
      borderRadius: 8,
      transition: "all 0.2s",
      ":hover": {
        borderColor: "#94a3b8",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
      },
    },
    categoryInfo: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      flex: 1,
    },
    categoryName: {
      fontSize: 15,
      fontWeight: 500,
      color: "#1a1a1a",
    },
    categoryPreview: {
      padding: "4px 10px",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 600,
    },
    categoryActions: {
      display: "flex",
      gap: 8,
    },
    iconButton: {
      background: "transparent",
      border: "none",
      padding: "8px",
      borderRadius: 6,
      cursor: "pointer",
      color: "#64748b",
      transition: "all 0.2s",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      ":hover": {
        background: "#f1f5f9",
      },
    },
    editButtonIcon: {
      ":hover": {
        color: "#2563eb",
        background: "#dbeafe",
      },
    },
    deleteButtonIcon: {
      ":hover": {
        color: "#dc2626",
        background: "#fee2e2",
      },
    },
    saveButton: {
      padding: "8px 16px",
      background: "#2563eb",
      border: "none",
      borderRadius: 6,
      fontSize: 13,
      fontWeight: 600,
      color: "white",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 4,
      ":hover": {
        background: "#1d4ed8",
      },
    },
    cancelButtonSmall: {
      padding: "8px 16px",
      background: "#f1f5f9",
      border: "1px solid #e2e8f0",
      borderRadius: 6,
      fontSize: 13,
      fontWeight: 600,
      color: "#64748b",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 4,
      ":hover": {
        background: "#e2e8f0",
      },
    },

    editInput: {
      flex: 1,
      padding: "8px 12px",
      fontSize: 14,
      border: "2px solid #2563eb",
      borderRadius: 6,
      outline: "none",
    },

    // Enkel delete-popup
    simplePopup: {
      background: "white",
      borderRadius: 12,
      width: "90%",
      maxWidth: 300,
      padding: "20px",
      boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
    },
    simplePopupText: {
      fontSize: 16,
      color: "#1a1a1a",
      marginBottom: 20,
      textAlign: "center",
    },
    simplePopupButtons: {
      display: "flex",
      gap: 10,
      justifyContent: "center",
    },
    simpleCancelButton: {
      padding: "8px 16px",
      background: "#f3f4f6",
      border: "1px solid #e5e7eb",
      borderRadius: 6,
      fontSize: 14,
      fontWeight: 500,
      color: "#4b5563",
      cursor: "pointer",
      flex: 1,
    },
    simpleDeleteButton: {
      padding: "8px 16px",
      background: "#dc2626",
      border: "none",
      borderRadius: 6,
      fontSize: 14,
      fontWeight: 500,
      color: "white",
      cursor: "pointer",
      flex: 1,
    },

    infoGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: 20,
      marginBottom: 24,
    },
    infoItem: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },
    infoLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    infoValue: {
      fontSize: 16,
      fontWeight: 600,
      color: "#1a1a1a",
    },
    notesSection: {
      background: "#f9fafb",
      borderRadius: 12,
      padding: "16px",
      marginTop: 8,
    },
    notesLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: "#6b7280",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      marginBottom: 12,
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    notesContent: {
      fontSize: 15,
      color: "#1f2937",
      lineHeight: 1.6,
      whiteSpace: "pre-wrap",
    },

    searchSelectWrapper: {
      display: "flex",
      alignItems: "center",
      width: 230,
      border: "1px solid #d1d5db",
      borderRadius: 6,
      background: "white",
      paddingRight: 6,
    },

    searchSelect: {
      flex: 1,
      border: "none",
      outline: "none",
      padding: "8px 8px 8px 12px",
      fontSize: 14,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },

    clearButton: {
      border: "none",
      background: "transparent",
      fontSize: 16,
      cursor: "pointer",
      color: "#9ca3af",
      padding: "0 6px",
      display: "flex",
      alignItems: "center",
    },

    actionButtonsContainer: {
      display: "flex",
      gap: 6,
      justifyContent: "flex-end",
    },

    popupActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 12,
      marginTop: 24,
    },
  };

  // Uppdatera sammanställning
  const updateSummary = (data) => {
    const readCount = data.filter(r => r.isRead).length;
    setSummary({
      totalReports: data.length,
      readReports: readCount,
      unreadReports: data.length - readCount,
    });
  };

  // Filtrera data (inklusive projektfilter)
  const filteredData = useMemo(() => {
    return readStatusData.filter(row => {
      if (selectedCategory && row.category?._id !== selectedCategory) return false;
      if (selectedEmployee && row.employee?._id !== selectedEmployee) return false;
      const projectId = row.project?._id || row.project;
      if (selectedProject && projectId !== selectedProject) return false;
      if (readFilter === "read" && !row.isRead) return false;
      if (readFilter === "unread" && row.isRead) return false;
      return true;
    });
  }, [readStatusData, selectedCategory, selectedEmployee, selectedProject, readFilter]);

  // Sortera efter projektnamn (bokstavsordning)
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const nameA = a.project?.name || a.projectName || '';
      const nameB = b.project?.name || b.projectName || '';
      return nameA.localeCompare(nameB, 'sv');
    });
  }, [filteredData]);

  // Totala timmar för alla filtrerade rapporter
  const totalHoursAll = useMemo(() => {
    return filteredData.reduce(
      (sum, report) => sum + calculateNetHours(report),
      0
    );
  }, [filteredData]);

  // Paginering
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(sortedData.length / PAGE_SIZE);
  const pagedData = sortedData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [sortedData, readFilter]);

  // Hantera markera som läst/oläst
  const handleToggleReadStatus = async (reportId, currentStatus, event) => {
    event.stopPropagation();
    try {
      const res = await axios.post(`${BACKEND_URL}/toggle-report-read`, {
        userId: user._id,
        companyId: company._id,
        reportId,
      }, { withCredentials: true });

      if (res.data.status === "success") {
        fetchReports();
      }
    } catch (error) {
      setToastType("error");
      setToastMessage("Kunde inte uppdatera status.");
    }
  };

  // Öppna popup med rapportdetaljer (visningsläge)
  const handleRowClick = (report) => {
    setSelectedReport(report);
    setEditingReport(null); // Stäng eventuellt redigeringsläge
  };

  // Stäng popup
  const handleClosePopup = () => {
    setSelectedReport(null);
    setEditingReport(null);
    setEditedReportData(null);
  };



  const handleEditClick = (report, e) => {
    e.stopPropagation();

    setEditingReport(report);
    setEditedReportData({
      ...report,
      employeeId: report.employee?._id || report.employee,
      categoryId: report.category?._id || null,
      projectId: report.project?._id || null,
      date: report.date,

      startTime: normalizeTime(report.startTime),
      endTime: normalizeTime(report.endTime),

      breaks: (report.breaks || []).map(b => ({
        startTime: normalizeTime(b.startTime),
        endTime: normalizeTime(b.endTime),
      })),
    });

    setSelectedReport(report);
  };

  // Spara redigerad rapport
  const handleSaveEdit = async () => {
    if (!editedReportData) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/update-report`, {
        userId: user._id,
        companyId: company._id,
        reportId: editedReportData._id,
        employeeId: editedReportData.employeeId,
        categoryId: editedReportData.categoryId,
        projectId: editedReportData.projectId,
        date: editedReportData.date,
        startTime: editedReportData.startTime,
        endTime: editedReportData.endTime,
        notes: editedReportData.notes,
        breaks: editedReportData.breaks,
      }, { withCredentials: true });

      if (res.data.status === "success") {
        setToastMessage("Rapport uppdaterad");
        setToastType("success");
        setEditingReport(null);
        setSelectedReport(null);
        setEditedReportData(null);
        fetchReports();
      } else {
        setToastMessage(res.data.errorText || "Kunde inte uppdatera");
        setToastType("error");
      }
    } catch (err) {
      console.error(err);
      setToastMessage("Ett fel inträffade");
      setToastType("error");
    }
  };

  // Ta bort rapport
  const handleDeleteClick = (report, e) => {
    e.stopPropagation();
    setReportToDelete(report);
    setShowDeleteReportConfirm(true);
  };

  const confirmDeleteReport = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/delete-report`, {
        userId: user._id,
        companyId: company._id,
        reportId: reportToDelete._id,
      }, { withCredentials: true });

      if (res.data.status === "success") {
        setToastMessage("Rapport borttagen");
        setToastType("success");
        setShowDeleteReportConfirm(false);
        setReportToDelete(null);
        fetchReports();
      } else {
        setToastMessage(res.data.errorText || "Kunde inte ta bort");
        setToastType("error");
      }
    } catch (err) {
      console.error(err);
      setToastMessage("Ett fel inträffade");
      setToastType("error");
    }
  };

  const cancelDeleteReport = () => {
    setShowDeleteReportConfirm(false);
    setReportToDelete(null);
  };

  // Hantera lägg till kategori
  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/create-report-category`, {
        userId: user._id,
        companyId: company._id,
        name: newCategory.trim(),
      }, { withCredentials: true });

      if (res.data.status === "success") {
        setNewCategory("");
        setReportCategories(res.data.categories);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleEditCategory = async (categoryId, newName) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/update-report-category`, {
        userId: user._id,
        companyId: company._id,
        categoryId,
        name: newName,
      }, { withCredentials: true });

      if (res.data.status === "success") {
        setReportCategories(res.data.categories);
        setEditingCategory(null);
      } else {
        alert(res.data.errorText || "fel inträffade");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getCategoryStyle = (name) => {
    if (!name) return { background: "#e5e7eb", color: "#374151" };
    return { background: "#e5e7eb", color: "#374151" };
  };

  const confirmDeleteCategory = (category) => {
    setCategoryToDelete(category);
    setShowDeletePopup(true);
  };

  const handleDeleteCategory = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/delete-report-category`, {
        userId: user._id,
        companyId: company._id,
        categoryId: categoryToDelete._id,
      }, { withCredentials: true });

      setShowDeletePopup(false);
      if (res.data.status === "success") {
        setReportCategories(res.data.categories);
        setSelectedCategory("");
      } else {
        alert(res.data.errorText || "fel inträffade");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const cancelDeleteCategory = () => {
    setShowDeletePopup(false);
    setCategoryToDelete(null);
  };

  const startEditingCategory = (category) => {
    setEditingCategory(category._id);
    setEditedCategoryName(category.name);
  };

  const cancelEditingCategory = () => {
    setEditingCategory(null);
    setEditedCategoryName("");
  };

  const handleOpenCategoryPopup = () => {
    setShowCategoryPopup(true);
    setNewCategory("");
    setEditingCategory(null);
    setEditedCategoryName("");
    setShowDeletePopup(false);
    setCategoryToDelete(null);
  };

  const handleCloseCategoryPopup = () => {
    setShowCategoryPopup(false);
    setNewCategory("");
    setEditingCategory(null);
    setEditedCategoryName("");
    setShowDeletePopup(false);
    setCategoryToDelete(null);
  };

  // Rendera tabellheader
  const renderTableHeader = () => (
    <>
      <span>Anställd</span>
      <span>Datum</span>
      <span>Projekt</span>
      <span>Kategori</span>
      <span>Timmar</span>
      <span>Status</span>
      <span style={styles.cellRight}>Åtgärd</span>
    </>
  );

  // Rendera tabellrad med redigera/ta bort-knappar
  const renderTableRow = (row) => (
    <>
      <span style={styles.cell}>{row.employee?.name || row.employee}</span>
      <span style={styles.cell}>
        <div>{formatReportDate(row.date)}</div>
      </span>
      <span style={styles.cell}>
        {row.project?.name || row.projectName || "-"}
      </span>
      <span style={styles.cell}>
        <span style={{
          ...styles.categoryBadge,
          ...getCategoryStyle(row.category?.name)
        }}>
          {row.category?.name}
        </span>
      </span>

      <span style={styles.cell}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            lineHeight: 1.25,
          }}
        >
          <span
            style={{
              fontWeight: 700,
              color: "#111827",
              fontSize: 14,
            }}
          >
            {calculateNetHours(row).toFixed(2)} h
          </span>

          <span
            style={{
              fontSize: 11,
              color: "#6B7280",
            }}
          >
            Arbetstid:
            {" "}
            {calculateHours(
              row.startTime,
              row.endTime
            ).toFixed(2)} h
            <br></br>
            Rast:
            {" "}
            {calculateBreakHours(row.breaks).toFixed(2)} h
          </span>
        </div>
      </span>

      <span style={styles.cell}>
        <div style={{
          ...styles.readStatus,
          ...(row.isRead ? styles.readStatusRead : styles.readStatusUnread)
        }}>
          {row.isRead ? <FaCheckCircle /> : <FaHourglassHalf />}
          {row.isRead ? "Läst" : "Oläst"}
        </div>
      </span>
      <span style={{...styles.cell, ...styles.cellRight}}>
        <div style={styles.actionButtonsContainer}>
          <button
            style={{...styles.actionButton, ...styles.editButton}}
            onClick={(e) => handleEditClick(row, e)}
            title="Redigera"
          >
            <FaEdit />
          </button>
          <button
            style={{...styles.actionButton, ...styles.deleteButton}}
            onClick={(e) => handleDeleteClick(row, e)}
            title="Ta bort"
          >
            <FaTrash />
          </button>
          <button
            style={{
              ...styles.actionButton,
              ...(row.isRead ? styles.markUnreadButton : styles.markReadButton)
            }}
            onClick={(e) => handleToggleReadStatus(row._id, row.isRead, e)}
          >
            {row.isRead ? <FaEyeSlash /> : <FaEye />}
            {row.isRead ? "Oläst" : "Läst"}
          </button>
        </div>
      </span>
    </>
  );

  return (
    <div style={styles.wrap}>
      <Nav/>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.pageTitle}>Rapporter</h1>
          <p style={styles.pageSubtitle}>Se vilka rapporter som är lästa och olästa</p>
        </div>

        {/* Summary Cards */}
        <div style={styles.statsContainer}>
          <div style={styles.statCard}>
            <FaFileAlt style={styles.statIcon} />
            <div style={styles.statLabel}>Totalt rapporter</div>
            <div style={styles.statValue}>{summary.totalReports}</div>
          </div>
          <div style={styles.statCard}>
            <FaCheckCircle style={{ ...styles.statIcon, color: "#10b981" }} />
            <div style={styles.statLabel}>Lästa</div>
            <div style={styles.statValue}>{summary.readReports}</div>
          </div>
          <div style={styles.statCard}>
            <FaHourglassHalf style={{ ...styles.statIcon, color: "#f59e0b" }} />
            <div style={styles.statLabel}>Olästa</div>
            <div style={styles.statValue}>{summary.unreadReports}</div>
          </div>
          <div style={styles.statCard}>
            <FaClock style={{ ...styles.statIcon, color: "#2563eb" }} />
            <div style={styles.statLabel}>Totalt timmar</div>
            <div style={styles.statValue}>{totalHoursAll.toFixed(2)} h</div>
          </div>
        </div>

        {/* Controls */}
        <div style={styles.controlsSection}>
          <div style={styles.dateSelector}>
            <FaCalendarAlt style={{ color: "#64748b" }} />
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(+e.target.value)} style={styles.select}>
              {monthsInSwedish.map((m, i) => (<option key={i} value={i + 1}>{m}</option>))}
            </select>
            <select value={selectedYear} onChange={(e) => setSelectedYear(+e.target.value)} style={styles.select}>
              {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div style={styles.filterGroup}>
            <div style={styles.categoryFilterWrapper}>
              <button 
                style={styles.manageCategoriesButton}
                onClick={handleOpenCategoryPopup}
              >
                <FaEdit /> Hantera kategorier
              </button>
            </div>

            <select 
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value)} 
              style={styles.select}
            >
              <option value="">Alla kategorier</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat._id}>{cat.name}</option>
              ))}
            </select>
            
            <div style={styles.searchSelectWrapper}>
              <input
                list="employeeList"
                value={employeeInput}
                placeholder="Alla anställda"
                onChange={(e) => {
                  const name = e.target.value;
                  setEmployeeInput(name);
                  const match = employees.find(emp => emp.name === name);
                  setSelectedEmployee(match ? match._id : "");
                }}
                style={styles.searchSelect}
              />
              {employeeInput && (
                <button
                  onClick={() => {
                    setEmployeeInput("");
                    setSelectedEmployee("");
                  }}
                  style={styles.clearButton}
                >
                  ×
                </button>
              )}
              <datalist id="employeeList">
                {employees.map(emp => (
                  <option key={emp._id} value={emp.name} />
                ))}
              </datalist>
            </div>

            <div style={styles.searchSelectWrapper}>
              <input
                list="projectList"
                value={projectInput}
                placeholder="Alla projekt"
                onChange={(e) => {
                  const name = e.target.value;
                  setProjectInput(name);
                  const match = projects.find(p => p.name === name);
                  setSelectedProject(match ? match._id : "");
                }}
                style={styles.searchSelect}
              />
              {projectInput && (
                <button
                  onClick={() => {
                    setProjectInput("");
                    setSelectedProject("");
                  }}
                  style={styles.clearButton}
                >
                  ×
                </button>
              )}
              <datalist id="projectList">
                {projects.map(p => (
                  <option key={p._id} value={p.name} />
                ))}
              </datalist>
            </div>

            <div style={styles.readFilterGroup}>
              <button
                style={styles.readFilterButton(readFilter === "all")}
                onClick={() => setReadFilter("all")}
              >
                <FaFilter /> Alla
              </button>
              <button
                style={styles.readFilterButton(readFilter === "unread")}
                onClick={() => setReadFilter("unread")}
              >
                <FaHourglassHalf /> Olästa
              </button>
              <button
                style={styles.readFilterButton(readFilter === "read")}
                onClick={() => setReadFilter("read")}
              >
                <FaCheckCircle /> Lästa
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div style={styles.tableContainer}>
          <div style={styles.tableHeader}>
            {renderTableHeader()}
          </div>

          {loading ? (
            <div style={styles.emptyState}>Laddar rapportdata...</div>
          ) : pagedData.length === 0 ? (
            <div style={styles.emptyState}>
              {selectedCategory || selectedEmployee || selectedProject || readFilter !== "all"
                ? "Ingen data matchar dina filter."
                : "Inga rapporter tillgängliga för vald period."}
            </div>
          ) : (
            pagedData.map((row) => (
              <div 
                key={row._id} 
                style={styles.tableRow}
                onClick={() => handleRowClick(row)}
              >
                {renderTableRow(row)}
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={styles.pagination}>
            <div style={styles.paginationInfo}>
              Visar {pagedData.length} av {sortedData.length} rapporter
            </div>
            <div style={styles.paginationControls}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ ...styles.pageBtn, opacity: page === 1 ? 0.5 : 1, cursor: page === 1 ? "not-allowed" : "pointer" }}
              >
                Föregående
              </button>
              <span style={{ fontSize: 14, color: "#6b7280", padding: "0 16px" }}>
                Sida {page} av {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{ ...styles.pageBtn, opacity: page === totalPages ? 0.5 : 1, cursor: page === totalPages ? "not-allowed" : "pointer" }}
              >
                Nästa
              </button>
            </div>
          </div>
        )}

        {/* Popup för att hantera kategorier */}
        {showCategoryPopup && (
          <div style={styles.overlay} onClick={handleCloseCategoryPopup}>
            <div style={styles.popup} onClick={(e) => e.stopPropagation()}>
              <div style={styles.popupHeader}>
                <h2 style={styles.popupTitle}>Hantera kategorier</h2>
                <button style={styles.closeButton} onClick={handleCloseCategoryPopup}>
                  <FaTimes />
                </button>
              </div>
              <div style={styles.popupContent}>
                {/* Lägg till ny kategori */}
                <div style={styles.addCategorySection}>
                  <h3 style={styles.sectionTitle}>
                    <FaPlus /> Lägg till ny kategori
                  </h3>
                  <div style={styles.formGroup}>
                    <input
                      type="text"
                      style={styles.input}
                      value={newCategory}
                      onChange={(e) => setNewCategory(e.target.value)}
                      placeholder="Skriv in kategori..."
                      onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                    />
                    <button style={styles.addButton} onClick={handleAddCategory}>
                      <FaPlus /> Lägg till
                    </button>
                  </div>
                </div>

                {/* Befintliga kategorier */}
                <h3 style={styles.sectionTitle}>
                  <FaTag /> Befintliga kategorier
                </h3>
                <div style={styles.categoryList}>
                  {categories.map((category) => (
                    <div key={category._id} style={styles.categoryItem}>
                      {editingCategory === category._id ? (
                        // Redigeringsläge
                        <>
                          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
                            <input
                              type="text"
                              style={styles.editInput}
                              value={editedCategoryName}
                              onChange={(e) => setEditedCategoryName(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleEditCategory(category._id, editedCategoryName)}
                              autoFocus
                            />
                            <span style={{
                              ...styles.categoryPreview,
                              ...getCategoryStyle(editedCategoryName || category.name)
                            }}>
                              Förhandsgranskning
                            </span>
                          </div>
                          <div style={styles.categoryActions}>
                            <button
                              style={{...styles.iconButton, ...styles.saveButton}}
                              onClick={() => handleEditCategory(category._id, editedCategoryName)}
                            >
                              <FaSave /> Spara
                            </button>
                            <button
                              style={{...styles.iconButton, ...styles.cancelButtonSmall}}
                              onClick={cancelEditingCategory}
                            >
                              <FaTimesCircle /> Avbryt
                            </button>
                          </div>
                        </>
                      ) : (
                        // Visningsläge
                        <>
                          <div style={styles.categoryInfo}>
                            <span style={styles.categoryName}>{category.name}</span>
                            <span style={{
                              ...styles.categoryPreview,
                              ...getCategoryStyle(category.name)
                            }}>
                              {category.name}
                            </span>
                          </div>
                          <div style={styles.categoryActions}>
                            <button
                              style={{...styles.iconButton, ...styles.editButtonIcon}}
                              onClick={() => startEditingCategory(category)}
                            >
                              <FaEdit />
                            </button>
                            <button
                              style={{...styles.iconButton, ...styles.deleteButtonIcon}}
                              onClick={() => confirmDeleteCategory(category)}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
 
        {/* Enkel popup för borttagning av kategori */}
        {showDeletePopup && (
          <div style={styles.overlay} onClick={cancelDeleteCategory}>
            <div style={styles.simplePopup} onClick={(e) => e.stopPropagation()}>
              <div style={styles.simplePopupText}>
                Ta bort "{categoryToDelete?.name}"?
              </div>
              <div style={styles.simplePopupButtons}>
                <button style={styles.simpleCancelButton} onClick={cancelDeleteCategory}>
                  Avbryt
                </button>
                <button style={styles.simpleDeleteButton} onClick={handleDeleteCategory}>
                  Ta bort
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Popup för rapportdetaljer (visning/redigering) */}
        {selectedReport && (
          <div style={styles.overlay} onClick={handleClosePopup}>
            <div
              style={{
                ...styles.popup,
                maxWidth: 1060,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  ...styles.popupHeader,
                  paddingBottom: 18,
                }}
              >
                <div>
                  <h2 style={styles.popupTitle}>
                    {editingReport
                      ? "Redigera rapport"
                      : "Rapportdetaljer"}
                  </h2>

                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 14,
                      color: "#64748B",
                      fontWeight: 500,
                    }}
                  >
                    {formatReportDate(selectedReport.date)}
                  </div>
                </div>

                <button
                  style={styles.closeButton}
                  onClick={handleClosePopup}
                >
                  <FaTimes />
                </button>
              </div>

              <div
                style={{
                  ...styles.popupContent,
                  paddingTop: 20,
                }}
              >
              {editingReport ? (
                <div style={styles.formGroup}>
                  <div style={styles.formRow}>
                    <label style={styles.label}>Anställd</label>
                    <select
                      style={styles.input}
                      value={editedReportData?.employeeId || ""}
                      onChange={(e) =>
                        setEditedReportData({
                          ...editedReportData,
                          employeeId: e.target.value,
                        })
                      }
                    >
                      <option value="">Välj anställd</option>
                      {employees.map(emp => (
                        <option key={emp._id} value={emp._id}>
                          {emp.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.formRowDouble}>
                    <div>
                      <label style={styles.label}>Kategori</label>
                      <select
                        style={styles.input}
                        value={editedReportData?.categoryId || ""}
                        onChange={(e) =>
                          setEditedReportData({
                            ...editedReportData,
                            categoryId: e.target.value,
                          })
                        }
                      >
                        <option value="">Välj kategori</option>
                        {categories.map(cat => (
                          <option key={cat._id} value={cat._id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={styles.label}>Projekt</label>
                      <select
                        style={styles.input}
                        value={editedReportData?.projectId || ""}
                        onChange={(e) =>
                          setEditedReportData({
                            ...editedReportData,
                            projectId: e.target.value,
                          })
                        }
                      >
                        <option value="">Välj projekt</option>
                        {projects.map(proj => (
                          <option key={proj._id} value={proj._id}>
                            {proj.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={styles.formRow}>
                    <label style={styles.label}>Datum</label>
                    <input
                      type="date"
                      style={styles.input}
                      value={editedReportData?.date || ""}
                      onChange={(e) =>
                        setEditedReportData({
                          ...editedReportData,
                          date: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div style={styles.formRowDouble}>
                    <div>
                      <label style={styles.label}>Starttid</label>
                      <input
                        type="time"
                        style={styles.input}
                        value={editedReportData?.startTime || ""}
                        onChange={(e) =>
                          setEditedReportData({
                            ...editedReportData,
                            startTime: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label style={styles.label}>Sluttid</label>
                      <input
                        type="time"
                        style={styles.input}
                        value={editedReportData?.endTime || ""}
                        onChange={(e) =>
                          setEditedReportData({
                            ...editedReportData,
                            endTime: e.target.value,
                          })
                        }
                      />
                    </div>


                    <div style={{marginTop:12}}>
                      <label style={styles.label}>Raster</label>

                      <div style={{display:"flex",flexDirection:"column",gap:8,marginTop:8}}>
                        {editedReportData.breaks?.map((breakItem,index) => (
                          <div
                            key={index}
                            style={{
                              display:"grid",
                              gridTemplateColumns:"1fr 1fr auto",
                              gap:8,
                              alignItems:"center",
                            }}
                          >
                            <input
                              type="time"
                              style={styles.input}
                              value={breakItem.startTime || ""}
                              onChange={(e) => {
                                const breaks = [...editedReportData.breaks];
                                breaks[index].startTime = e.target.value;

                                setEditedReportData({
                                  ...editedReportData,
                                  breaks,
                                });
                              }}
                            />

                            <input
                              type="time"
                              style={styles.input}
                              value={breakItem.endTime || ""}
                              onChange={(e) => {
                                const breaks = [...editedReportData.breaks];
                                breaks[index].endTime = e.target.value;

                                setEditedReportData({
                                  ...editedReportData,
                                  breaks,
                                });
                              }}
                            />

                            <button
                              type="button"
                              onClick={() => {
                                const breaks = editedReportData.breaks.filter(
                                  (_, i) => i !== index
                                );

                                setEditedReportData({
                                  ...editedReportData,
                                  breaks,
                                });
                              }}
                              style={{
                                width:42,
                                height:42,
                                border:"none",
                                borderRadius:10,
                                background:"#FEE2E2",
                                color:"#DC2626",
                                cursor:"pointer",
                                display:"flex",
                                alignItems:"center",
                                justifyContent:"center",
                                flexShrink:0,
                              }}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setEditedReportData({
                            ...editedReportData,
                            breaks:[
                              ...(editedReportData.breaks || []),
                              {
                                startTime:"",
                                endTime:"",
                              },
                            ],
                          });
                        }}
                        style={{
                          marginTop:10,
                          border:"none",
                          background:"#EFF6FF",
                          color:"#2563EB",
                          padding:"10px 14px",
                          borderRadius:10,
                          fontWeight:600,
                          cursor:"pointer",
                          display:"flex",
                          alignItems:"center",
                          gap:8,
                        }}
                      >
                        <FaPlus />
                        Lägg till rast
                      </button>
                    </div>
                  </div>

                  <div style={styles.formRow}>
                    <label style={styles.label}>Noteringar</label>
                    <textarea
                      style={styles.textarea}
                      value={editedReportData?.notes || ""}
                      onChange={(e) =>
                        setEditedReportData({
                          ...editedReportData,
                          notes: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div style={styles.popupActions}>
                    <button
                      style={styles.cancelButton}
                      onClick={() => setEditingReport(null)}
                    >
                      Avbryt
                    </button>

                    <button
                      style={styles.addButton}
                      onClick={handleSaveEdit}
                    >
                      <FaSave />
                      Spara
                    </button>
                  </div>
                </div>
              ) : (
                  <>
                    {/* SUMMARY */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "1.25fr 1fr 1fr",
                        gap: 14,
                        marginBottom: 22,
                      }}
                    >
                      {/* TOTAL */}
                      <div
                        style={{
                          background:
                            "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                          borderRadius: 18,
                          padding: "22px",
                          color: "white",
                          boxShadow:
                            "0 10px 25px rgba(37,99,235,0.18)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            opacity: 0.82,
                            marginBottom: 10,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                          }}
                        >
                          Totalt timmar
                        </div>

                        <div
                          style={{
                            fontSize: 42,
                            fontWeight: 800,
                            lineHeight: 1,
                            letterSpacing: "-0.04em",
                          }}
                        >
                          {calculateNetHours(
                            selectedReport
                          ).toFixed(2)}
                        </div>

                        <div
                          style={{
                            marginTop: 8,
                            fontSize: 13,
                            opacity: 0.85,
                          }}
                        >
                          efter raster
                        </div>
                      </div>

                      {/* ARBETSTID */}
                      <div
                        style={{
                          background: "#F8FAFC",
                          border: "1px solid #E2E8F0",
                          borderRadius: 18,
                          padding: "20px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#64748B",
                            marginBottom: 10,
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                          }}
                        >
                          Arbetstid
                        </div>

                        <div
                          style={{
                            fontSize: 34,
                            fontWeight: 800,
                            color: "#111827",
                            lineHeight: 1,
                            letterSpacing: "-0.04em",
                          }}
                        >
                          {calculateHours(
                            selectedReport.startTime,
                            selectedReport.endTime
                          ).toFixed(2)}
                        </div>

                        <div
                          style={{
                            marginTop: 8,
                            fontSize: 13,
                            color: "#64748B",
                          }}
                        >
                          före raster
                        </div>
                      </div>

                      {/* RAST */}
                      <div
                        style={{
                          background: "#F8FAFC",
                          border: "1px solid #E2E8F0",
                          borderRadius: 18,
                          padding: "20px",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: "#64748B",
                            marginBottom: 10,
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                          }}
                        >
                          Rasttimmar
                        </div>

                        <div
                          style={{
                            fontSize: 34,
                            fontWeight: 800,
                            color: "#111827",
                            lineHeight: 1,
                            letterSpacing: "-0.04em",
                          }}
                        >
                          {calculateBreakHours(
                            selectedReport.breaks
                          ).toFixed(2)}
                        </div>

                        <div
                          style={{
                            marginTop: 8,
                            fontSize: 13,
                            color: "#64748B",
                          }}
                        >
                          avdraget
                        </div>
                      </div>
                    </div>

                    {/* MAIN CONTENT */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "1.1fr 0.9fr",
                        gap: 18,
                        alignItems: "start",
                      }}
                    >
                      {/* LEFT */}
                      <div
                        style={{
                          background: "#F8FAFC",
                          border: "1px solid #E2E8F0",
                          borderRadius: 18,
                          padding: 20,
                        }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "1fr 1fr",
                            gap: 18,
                          }}
                        >
                          <div>
                            <div style={styles.infoLabel}>
                              <FaUser /> Anställd
                            </div>

                            <div style={styles.infoValue}>
                              {selectedReport.employee
                                ?.name ||
                                selectedReport.employee}
                            </div>
                          </div>

                          <div>
                            <div style={styles.infoLabel}>
                              <FaCalendarAlt /> Rapportdatum
                            </div>

                            <div style={styles.infoValue}>
                              {formatReportDate(
                                selectedReport.date
                              )}
                            </div>
                          </div>

                          <div>
                            <div style={styles.infoLabel}>
                              <FaClock /> Arbetstid
                            </div>

                            <div style={styles.infoValue}>
                              {normalizeTime(
                                selectedReport.startTime
                              )}
                              {" → "}
                              {normalizeTime(
                                selectedReport.endTime
                              )}
                            </div>
                          </div>

                          <div>
                            <div style={styles.infoLabel}>
                              <FaFileAlt /> Projekt
                            </div>

                            <div style={styles.infoValue}>
                              {selectedReport.project
                                ?.name ||
                                selectedReport.projectName ||
                                "-"}
                            </div>
                          </div>

                          <div>
                            <div style={styles.infoLabel}>
                              <FaTag /> Kategori
                            </div>

                            <div
                              style={{
                                marginTop: 6,
                              }}
                            >
                              <span
                                style={{
                                  ...styles.categoryBadge,
                                  ...getCategoryStyle(
                                    selectedReport.category
                                      ?.name
                                  ),
                                }}
                              >
                                {
                                  selectedReport.category
                                    ?.name
                                }
                              </span>
                            </div>
                          </div>

                          <div>
                            <div style={styles.infoLabel}>
                              <FaCalendarAlt /> Inskickad
                            </div>

                            <div style={styles.infoValue}>
                              {formatDateTime(
                                selectedReport.createdAt
                              )}
                            </div>
                          </div>
                        </div>

                        {/* NOTES */}
                        {selectedReport.notes && (
                          <div
                            style={{
                              marginTop: 22,
                              paddingTop: 20,
                              borderTop:
                                "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                ...styles.notesLabel,
                                marginBottom: 10,
                              }}
                            >
                              <FaCommentAlt /> Noteringar
                            </div>

                            <div
                              style={{
                                fontSize: 15,
                                lineHeight: 1.7,
                                color: "#1F2937",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {selectedReport.notes}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* RIGHT / BREAKS */}
                      <div
                        style={{
                          background: "#F8FAFC",
                          border: "1px solid #E2E8F0",
                          borderRadius: 18,
                          padding: 18,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                              "space-between",
                            marginBottom: 16,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: "#374151",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                            }}
                          >
                            Raster
                          </div>

                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: "#64748B",
                              background: "white",
                              border:
                                "1px solid #E5E7EB",
                              borderRadius: 999,
                              padding: "4px 10px",
                            }}
                          >
                            {selectedReport.breaks
                              ?.length || 0}{" "}
                            st
                          </div>
                        </div>

                        {Array.isArray(
                          selectedReport.breaks
                        ) &&
                        selectedReport.breaks.length >
                          0 ? (
                          <div
                            style={{
                              display: "flex",
                              flexDirection:
                                "column",
                              gap: 10,
                            }}
                          >
                            {selectedReport.breaks.map(
                              (
                                breakItem,
                                index
                              ) => (
                                <div
                                  key={index}
                                  style={{
                                    background:
                                      "white",
                                    border:
                                      "1px solid #E5E7EB",
                                    borderRadius: 14,
                                    padding:
                                      "14px 14px",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent:
                                        "space-between",
                                      alignItems:
                                        "center",
                                      marginBottom: 8,
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontWeight: 700,
                                        color:
                                          "#111827",
                                        fontSize: 14,
                                      }}
                                    >
                                      Rast{" "}
                                      {index + 1}
                                    </div>

                                    <div
                                      style={{
                                        fontWeight: 700,
                                        color:
                                          "#2563EB",
                                        fontSize: 14,
                                      }}
                                    >
                                      {calculateHours(
                                        breakItem.startTime,
                                        breakItem.endTime
                                      ).toFixed(
                                        2
                                      )}{" "}
                                      h
                                    </div>
                                  </div>

                                  <div
                                    style={{
                                      color:
                                        "#6B7280",
                                      fontSize: 14,
                                      fontWeight: 500,
                                    }}
                                  >
                                    {normalizeTime(
                                      breakItem.startTime
                                    )}
                                    {" → "}
                                    {normalizeTime(
                                      breakItem.endTime
                                    )}
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        ) : (
                          <div
                            style={{
                              background: "white",
                              border:
                                "1px dashed #CBD5E1",
                              borderRadius: 14,
                              padding: "18px",
                              textAlign: "center",
                              color: "#64748B",
                              fontSize: 14,
                              fontWeight: 500,
                            }}
                          >
                            Inga raster registrerade
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}



        {/* Bekräftelsepopup för borttagning av rapport */}
        {showDeleteReportConfirm && (
          <div style={styles.overlay} onClick={cancelDeleteReport}>
            <div style={styles.simplePopup} onClick={(e) => e.stopPropagation()}>
              <div style={styles.simplePopupText}>
                Ta bort rapport från {reportToDelete?.employee?.name}?
              </div>
              <div style={styles.simplePopupButtons}>
                <button style={styles.simpleCancelButton} onClick={cancelDeleteReport}>
                  Avbryt
                </button>
                <button style={styles.simpleDeleteButton} onClick={confirmDeleteReport}>
                  Ta bort
                </button>
              </div>
            </div>
          </div>
        )}

        <Toast message={toastMessage} type={toastType} onClose={() => setToastMessage("")} />
      </div>
    </div>
  );
};

export default Reports;